"use client";

/**
 * ────────────────────────────────────────────────────────────────────────────
 * DEMO SHELL — marco de teléfono (mockup iPhone) para la demo
 * ────────────────────────────────────────────────────────────────────────────
 * SÓLO se monta cuando `NEXT_PUBLIC_DEMO_SHELL === "1"` (el layout raíz lo
 * envuelve condicionalmente; sin la env este componente ni se importa en runtime
 * y la app se comporta como hoy). Ver `.env.local` y `next.config.ts` (las
 * cabeceras anti-clickjacking se aflojan a `self` con la misma env).
 *
 * ARQUITECTURA (por qué un <iframe> y no un <div> de 390px):
 *   Los breakpoints de Tailwind (`md:`…) son por VIEWPORT, no por contenedor. Un
 *   div de 390px dentro de un viewport de 1440px seguiría aplicando estilos de
 *   desktop. Un <iframe> del MISMO origen crea su PROPIO viewport (~390px), así
 *   que la app adentro renderiza como MÓVIL REAL (bottom nav, layouts mobile) y
 *   comparte cookies (misma sesión) con el top-level.
 *
 * TRES MODOS (decididos en el cliente; el server no conoce ancho ni si está
 * enmarcado):
 *   - `frame`   → top-level + desktop (≥ FRAME_MIN_WIDTH): renderiza SÓLO el
 *                 mockup con un <iframe> apuntando a la MISMA ruta + `?framed=1`.
 *                 NO renderiza children (los renderiza el iframe).
 *   - `bare`    → dentro del iframe, o móvil, o top-level angosto: renderiza
 *                 children normal (la app real; en el iframe el viewport es
 *                 ~390px → estilos móviles correctos). El iframe se auto-detecta
 *                 con `window.self !== window.top`, así que NO se re-envuelve.
 *   - `pending` → estado inicial (SSR + primer render cliente). Renderiza
 *                 children invisibles (opacity 0) para EVITAR el flash del layout
 *                 desktop full-width antes de decidir. Como el server siempre
 *                 renderiza children, la hidratación calza (sin mismatch).
 */

import {
  useEffect,
  useLayoutEffect,
  useState,
  type ReactNode,
} from "react";

/** Ancho de viewport (top-level) a partir del cual mostramos el marco. */
const FRAME_MIN_WIDTH = 700;

/** Tamaño lógico de la pantalla del teléfono (px CSS). Aspecto tipo iPhone. */
const SCREEN_W = 390;
const SCREEN_H = 844;
/** Grosor del bisel. */
const BEZEL = 13;
/**
 * Alto de la barra de estado (área de la Dynamic Island). El iframe arranca
 * DEBAJO de esta franja, así el contenido de la app NUNCA queda tapado por la
 * isla — cada rol puede usar su top-bar sin pelear con el shell. El viewport
 * interno del iframe sigue siendo ~390px de ancho (móvil real).
 */
const STATUS_BAR = 44;

/** useLayoutEffect en cliente (decide antes del paint), useEffect en SSR. */
const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

type Mode = "pending" | "frame" | "bare";

export function DemoShell({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Mode>("pending");
  // Escala del teléfono para caber en viewports bajos (transform: scale). No
  // cambia el viewport interno del iframe (sigue siendo ~390px → móvil real).
  const [scale, setScale] = useState(1);

  useIsoLayoutEffect(() => {
    // Same-origin: si difieren, estamos dentro del iframe → siempre `bare`.
    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch {
      // Cross-origin (no debería pasar en mismo-origen): asumimos enmarcado.
      inIframe = true;
    }

    const decide = () => {
      if (inIframe) {
        setMode("bare");
        return;
      }
      const desktop = window.innerWidth >= FRAME_MIN_WIDTH;
      if (!desktop) {
        setMode("bare");
        return;
      }
      const outerH = SCREEN_H + BEZEL * 2;
      setScale(Math.min(1, (window.innerHeight - 40) / outerH));
      setMode("frame");
    };

    decide();
    window.addEventListener("resize", decide);
    return () => window.removeEventListener("resize", decide);
  }, []);

  if (mode === "frame") {
    return <PhoneFrame scale={scale} />;
  }

  // `pending` (invisible, anti-flash) o `bare` (visible, la app real).
  //
  // El reveal es INSTANTÁNEO (sin transición de opacidad) a propósito: `decide()`
  // corre en useLayoutEffect ANTES del primer paint, así que el salto de
  // `pending`→`bare` no produce flash visible. Una transición de opacidad, en
  // cambio, se PAUSA mientras el documento está `visibilityState:"hidden"` (tab
  // en segundo plano, iframe fuera de foco, render headless) y puede dejar la app
  // congelada en opacity 0 → pantalla en blanco. Sin transición, la opacidad es
  // siempre el valor final de `mode` y la app nunca queda invisible.
  return (
    <div
      data-demo-ready={mode === "bare" ? "true" : "false"}
      style={{ opacity: mode === "bare" ? 1 : 0 }}
    >
      {children}
    </div>
  );
}

/**
 * Marco de teléfono estilo iPhone con un <iframe> que carga la MISMA ruta que el
 * top-level (así los deep links funcionan adentro) + `?framed=1`. El iframe
 * comparte origen y cookies con el top-level.
 */
function PhoneFrame({ scale }: { scale: number }) {
  // Sólo se monta en cliente (post-decisión), así que `window` existe.
  const [src] = useState(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("framed", "1");
    return url.pathname + url.search;
  });

  const outerW = SCREEN_W + BEZEL * 2;
  const outerH = SCREEN_H + BEZEL * 2;

  return (
    <div
      aria-hidden={false}
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Escenario de escritorio: hueso/arena cálido con un halo suave.
        background:
          "radial-gradient(120% 120% at 50% 0%, #fbf8f2 0%, #f0e8da 55%, #e6dbc8 100%)",
        overflow: "hidden",
        zIndex: 0,
      }}
    >
      {/* Marca sutil arriba a la izquierda del escenario */}
      <div
        style={{
          position: "absolute",
          top: 24,
          left: 28,
          display: "flex",
          alignItems: "center",
          gap: 10,
          color: "#2c4a3b",
          opacity: 0.85,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            width: 30,
            height: 30,
            borderRadius: 999,
            background: "#2c4a3b",
            color: "#fbf8f2",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LeafMark />
        </span>
        <span
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: 18,
            letterSpacing: "-0.01em",
            color: "#29241d",
          }}
        >
          Sanar
        </span>
      </div>

      {/* Teléfono (escalado para caber en viewports bajos) */}
      <div
        style={{
          width: outerW,
          height: outerH,
          transform: `scale(${scale})`,
          transformOrigin: "center center",
        }}
      >
        <div
          style={{
            position: "relative",
            width: outerW,
            height: outerH,
            borderRadius: 58,
            padding: BEZEL,
            background: "linear-gradient(160deg, #232019 0%, #12100c 100%)",
            boxShadow:
              "0 40px 90px -20px rgba(41,36,29,0.45), 0 8px 24px -8px rgba(41,36,29,0.35), inset 0 0 0 2px rgba(255,255,255,0.04)",
          }}
        >
          {/* Botones laterales */}
          <span style={sideBtn(112, "left")} />
          <span style={sideBtn(168, "left", 58)} />
          <span style={sideBtn(238, "left", 58)} />
          <span style={sideBtn(150, "right", 76)} />

          {/* Pantalla */}
          <div
            style={{
              position: "relative",
              width: SCREEN_W,
              height: SCREEN_H,
              borderRadius: 46,
              overflow: "hidden",
              background: "#fbf8f2",
            }}
          >
            {/* App real — arranca debajo de la barra de estado. */}
            <iframe
              title="Sanar — demo"
              src={src}
              allow="geolocation; camera"
              style={{
                position: "absolute",
                top: STATUS_BAR,
                left: 0,
                width: SCREEN_W,
                height: SCREEN_H - STATUS_BAR,
                border: "0",
                display: "block",
              }}
            />

            {/* Barra de estado faux (hora + iconos) sobre franja hueso, para que
                la isla nunca tape contenido de la app. */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: STATUS_BAR,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 27px",
                background: "#fbf8f2",
                zIndex: 2,
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: 14,
                  fontWeight: 600,
                  letterSpacing: "0.01em",
                  color: "#29241d",
                }}
              >
                9:41
              </span>
              <StatusIcons />
            </div>

            {/* Dynamic Island — pill oscura centrada en la barra de estado. */}
            <div
              style={{
                position: "absolute",
                top: 9,
                left: "50%",
                transform: "translateX(-50%)",
                width: 114,
                height: 30,
                borderRadius: 999,
                background: "#0a0a0a",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingRight: 12,
                zIndex: 3,
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: "#1c2b33",
                  boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,0.06)",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pie sutil del escenario */}
      <p
        style={{
          position: "absolute",
          bottom: 22,
          left: 0,
          right: 0,
          textAlign: "center",
          fontSize: 12,
          color: "#7a6f5e",
          letterSpacing: "0.02em",
        }}
      >
        Vista previa · Sanar
      </p>
    </div>
  );
}

/** Botón lateral del teléfono (volumen / power) como detalle del bisel. */
function sideBtn(
  top: number,
  side: "left" | "right",
  height = 34,
): React.CSSProperties {
  return {
    position: "absolute",
    top,
    [side]: -2,
    width: 3,
    height,
    borderRadius: 3,
    background: "linear-gradient(180deg, #2a261e, #14120d)",
  };
}

/** Iconos de la barra de estado (señal + wifi + batería), tono tinta cálida. */
function StatusIcons() {
  return (
    <svg width={62} height={14} viewBox="0 0 62 14" fill="none" aria-hidden="true">
      {/* Señal */}
      <g fill="#29241d">
        <rect x="0" y="8" width="3" height="4" rx="1" />
        <rect x="5" y="6" width="3" height="6" rx="1" />
        <rect x="10" y="3.5" width="3" height="8.5" rx="1" />
        <rect x="15" y="1" width="3" height="11" rx="1" />
      </g>
      {/* Wifi */}
      <g stroke="#29241d" strokeWidth="1.4" fill="none" strokeLinecap="round">
        <path d="M24 5.2c3.2-2.6 7.6-2.6 10.8 0" />
        <path d="M26.2 7.6c1.9-1.5 4.5-1.5 6.4 0" />
      </g>
      <circle cx="29.4" cy="10.2" r="1.1" fill="#29241d" />
      {/* Batería */}
      <g>
        <rect x="42" y="2" width="17" height="10" rx="2.6" stroke="#29241d" strokeWidth="1.2" fill="none" opacity="0.55" />
        <rect x="43.4" y="3.4" width="13" height="7.2" rx="1.4" fill="#29241d" />
        <rect x="60" y="5" width="1.6" height="4" rx="0.8" fill="#29241d" opacity="0.55" />
      </g>
    </svg>
  );
}

/** Monograma de hoja inline (mismo trazo que el set de íconos). */
function LeafMark() {
  return (
    <svg
      width={17}
      height={17}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 20A7 7 0 0 1 4 13C4 8 8 4 20 4c0 12-4 16-9 16Z" />
      <path d="M11 20c0-4 1-8 5-11" />
    </svg>
  );
}
